import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { IProfilePersonalDetailsApiResponse } from '../../../../apis/profile/profilePersonalDetails.interfaces';
import { useProfilePersonalDetailsHook } from '../hooks/useProfilePersonalDetailsHook';
import { localStorageGet } from '../../../../utils/localStorage';
import { ILoggedInUser } from '../../../../apis/auth/auth.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

type TProfilePersonalDetailsState = {
  personalDetails: IProfilePersonalDetailsApiResponse | null;
  editedPersonalDetails: IProfilePersonalDetailsApiResponse | null;
  isEditMode: boolean;
  isLoading: boolean;
  errorMessage?: string;
};

enum ACTION_TYPE {
  SET_PERSONAL_DETAILS = 'SET_PERSONAL_DETAILS',
  SET_EDITED_PERSONAL_DETAILS = 'SET_EDITED_PERSONAL_DETAILS',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  FETCHING = 'FETCHING',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | {
      type: ACTION_TYPE.SET_PERSONAL_DETAILS;
      payload: IProfilePersonalDetailsApiResponse | null;
    }
  | {
      type: ACTION_TYPE.SET_EDITED_PERSONAL_DETAILS;
      payload: IProfilePersonalDetailsApiResponse | null;
    }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.FETCHING; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialState: TProfilePersonalDetailsState = {
  personalDetails: null,
  editedPersonalDetails: null,
  isEditMode: false,
  isLoading: false,
  errorMessage: undefined,
};

const ProfilePersonalDetailsContext = createContext<
  TProfilePersonalDetailsState | undefined
>(undefined);
const ProfilePersonalDetailsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const profilePersonalDetailsReducer = (
  state: TProfilePersonalDetailsState,
  action: Action
): TProfilePersonalDetailsState => {
  switch (action.type) {
    case ACTION_TYPE.SET_PERSONAL_DETAILS:
      return {
        ...state,
        personalDetails: action.payload || null,
        errorMessage: undefined,
      };
    case ACTION_TYPE.SET_EDITED_PERSONAL_DETAILS:
      return {
        ...state,
        editedPersonalDetails: action.payload || null,
      };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return {
        ...state,
        isEditMode: action.payload,
      };
    case ACTION_TYPE.FETCHING:
      return {
        ...state,
        isLoading: action.payload,
        errorMessage: action.payload ? undefined : state.errorMessage,
      };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

export const ProfilePersonalDetailsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    profilePersonalDetailsReducer,
    initialState
  );

  const withMockData = isAppWithMock();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = withMockData
    ? '1234567890'
    : userFromStorage?.id || '';

  const {
    data: personalDetailsResponse,
    isLoading,
    error,
  } = useProfilePersonalDetailsHook(loggedInUserId, {
    enabled: !!loggedInUserId,
  });

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (personalDetailsResponse) {
      dispatch({
        type: ACTION_TYPE.SET_PERSONAL_DETAILS,
        payload: personalDetailsResponse,
      });
    }
  }, [personalDetailsResponse]);

  useEffect(() => {
    if (error) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          error?.response?.data?.message || 'Failed to fetch personal details',
      });
    }
  }, [error]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
    }),
    [state, isLoading]
  );

  return (
    <ProfilePersonalDetailsContext.Provider value={value}>
      <ProfilePersonalDetailsDispatchContext.Provider value={dispatch}>
        {children}
      </ProfilePersonalDetailsDispatchContext.Provider>
    </ProfilePersonalDetailsContext.Provider>
  );
};

export const useProfilePersonalDetailsState = () => {
  const context = useContext(ProfilePersonalDetailsContext);
  if (context === undefined) {
    throw new Error(
      'useProfilePersonalDetailsState must be used within a ProfilePersonalDetailsProvider'
    );
  }
  return context;
};

export const useProfilePersonalDetailsActions = () => {
  const dispatch = useContext(ProfilePersonalDetailsDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useProfilePersonalDetailsActions must be used within a ProfilePersonalDetailsProvider'
    );
  }

  const refreshPersonalDetails = () => {
    // This can be extended if needed for manual refresh
    // For now, the hook will automatically refetch when enabled
  };

  const setEditedPersonalDetails = (
    editedPersonalDetails: IProfilePersonalDetailsApiResponse | null
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_EDITED_PERSONAL_DETAILS,
      payload: editedPersonalDetails,
    });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  return {
    refreshPersonalDetails,
    setEditedPersonalDetails,
    setIsEditMode,
  };
};
